
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { arkOneApi } from '@/api'
import {
  transformPortfolioData,
  transformPositions,
  transformChartData
} from '@/utils/arkOneTransform'
import type {
  PortfolioData,
  PerformanceData,
  Position,
  RebalancingRecord,
  TimeRange,
  ColorMode,
  ChartData
} from '@/types/arkOne'
import StrategyHeader from '@/components/arkOne/StrategyHeader.vue'
import PerformanceChart from '@/components/arkOne/PerformanceChart.vue'
import CapitalDistribution from '@/components/arkOne/CapitalDistribution.vue'
import PositionDistribution from '@/components/arkOne/PositionDistribution.vue'
import PositionTabs from '@/components/arkOne/PositionTabs.vue'
import { zh_tran, objTran } from '@/utils/lang.js'
import WXQuotationWebSocket from '@/websocket/quotationWebsocket'

type Language = 'zh-Hans' | 'zh-Hant'

interface ErrorState {
  type: 'network' | 'api' | 'auth' | 'unknown'
  message: string
  retryable: boolean
}

const __default__ = defineComponent({
  name: 'ArkOneStrategyPage',
  components: {
    StrategyHeader,
    PerformanceChart,
    CapitalDistribution,
    PositionDistribution,
    PositionTabs
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    
    // 主题：根据路由参数 theme 判断，默认深色（黑色）
    const theme = (route.query.theme as string) || 'dark'
    
    // 延时行情标识
    const hkDelay = route.query.HKDELAY === '1'
    const usDelay = route.query.USDELAY === '1'
    
    // 语言切换：根据路由参数 Accept-Language 判断，zh-Hant 为繁体，默认 zh-Hans 为简体
    const langParam = (route.query['Accept-Language'] as string) || 'zh-Hans'
    const language = ref<Language>(langParam as Language)
    const isTraditional = computed(() => language.value === 'zh-Hant')
    
    // 状态
    const loading = ref(true)
    const error = ref<ErrorState | null>(null)
    const portfolioData = ref<PortfolioData | null>(null)
    const performanceData = ref<PerformanceData[]>([])
    const positions = ref<Position[]>([])
    const selectedTimeRange = ref<TimeRange>('1Y')
    const selectedRebalancingDate = ref<string | null>(null)
    
    // 颜色模式（从全局设置获取，这里暂时硬编码）
    const colorMode = ref<ColorMode>('red-rise-green-fall')
    
    // WebSocket 实例
    let wsInstance: WXQuotationWebSocket | null = null
    
    // 防止重复初始化的标志（使用ref确保在组件生命周期内保持状态）
    const isInitialized = ref(false)
    
    // 初始化时设置语言模式：仅繁体才做转换
    if (isTraditional.value) {
      zh_tran('t')
    }

    // 计算调仓日期列表
    const rebalancingDates = computed(() => {
      return performanceData.value
        .filter(item => item.rebalancing)
        .map(item => item.date)
    })

    // 处理API错误
    const handleApiError = (err: any): ErrorState => {
      if (err.response) {
        if (err.response.status === 401) {
          return {
            type: 'auth',
            message: '请先登录',
            retryable: false
          }
        } else if (err.response.status >= 500) {
          return {
            type: 'api',
            message: '服务器错误，请稍后重试',
            retryable: true
          }
        }
      } else if (err.request) {
        return {
          type: 'network',
          message: '网络连接失败，请检查网络',
          retryable: true
        }
      }
      
      return {
        type: 'unknown',
        message: '未知错误',
        retryable: true
      }
    }

    // 获取组合数据
    const fetchPortfolioData = async () => {
      try {
        const response = await arkOneApi.getPortfolioData({
          params: {id: 1},
          headers: {}
        })
        
        if (response.data && response.code === 0) {
          portfolioData.value = transformPortfolioData(response.data)
          
          // 仅繁体模式才转换数据；简体模式保持接口原样
          if (isTraditional.value && portfolioData.value) {
            portfolioData.value = objTran('t', portfolioData.value) as PortfolioData
          }
          
          // 同时转换持仓明细数据（使用同一个数据源）
          positions.value = transformPositions(response.data)
          
          // 仅繁体模式才转换数据；简体模式保持接口原样
          if (isTraditional.value && positions.value) {
            positions.value = objTran('t', positions.value) as Position[]
          }
          
          // 图表数据统一走 /fz/charts，这里不再从 /fz/detail 的 chart 中取
        } else {
          console.warn('获取组合数据失败:', response.message || '未知错误')
        }
      } catch (err) {
        console.error('获取组合数据异常:', err)
        throw err
      }
    }

    // 时间范围到 period_type 的映射
    // 0:近一周, 1:近一月, 2:上线至今, 3:近三月, 4:近一年, 5:近6月
    const getTimeRangePeriodType = (range: TimeRange): number => {
      const mapping: Record<TimeRange, number> = {
        '1M': 1,   // 近一月
        '3M': 3,   // 近三月
        '6M': 5,   // 近6月
        '1Y': 4,   // 近一年
        'ALL': 2   // 上线至今
      }
      return mapping[range] || 2 // 默认返回上线至今
    }

    // 获取图表数据（用于时间范围切换时更新图表）
    const fetchChartData = async () => {
      try {
        const response: any = await arkOneApi.getPositions({
          params: {
            period_type: getTimeRangePeriodType(selectedTimeRange.value)
          },
          headers: {}
        })

        if (response && response.code === 0 && response.data) {
          // /fz/charts 返回的 data 即 ChartData 对象
          const chartDataArray = transformChartData(response.data)
          if (chartDataArray.length > 0) {
            performanceData.value = chartDataArray
          }
        } else {
          console.warn('获取图表数据失败:', response?.message || '未知错误', {
            code: response?.code,
            range: selectedTimeRange.value,
            period_type: getTimeRangePeriodType(selectedTimeRange.value)
          })
        }
      } catch (err) {
        console.error('获取图表数据异常:', err)
        throw err
      }
    }

    // 获取实时价格
    const fetchRealTimePrices = async () => {
      if (positions.value.length === 0) return
      
      try {
        const codes = positions.value.map(p => p.code)
        const response = await arkOneApi.getRealTimePrices({
          data: { codes },
          headers: {}
        })
        
        if (response.data && response.data.code === 0) {
          const prices = response.data.data
          positions.value.forEach(position => {
            const priceData = prices.find((p: any) => p.code === position.code)
            if (priceData) {
              position.currentPrice = priceData.price
            }
          })
        }
      } catch (err) {
        console.error('获取实时价格失败:', err)
      }
    }

    // 获取所有数据
    const fetchAllData = async () => {
      // 防止重复初始化
      if (isInitialized.value) {
        console.log('已初始化，跳过重复初始化')
        return
      }
      isInitialized.value = true
      
      loading.value = true
      error.value = null
      
      try {
        // 先获取组合基础信息（不再用其中的 chart 画图）
        const results = await Promise.allSettled([fetchPortfolioData()])
        
        // 检查是否所有请求都失败了
        const allFailed = results.every(result => result.status === 'rejected')
        
        if (allFailed) {
          // 如果所有请求都失败，显示错误信息
          const firstError = results.find(r => r.status === 'rejected') as PromiseRejectedResult
          error.value = handleApiError(firstError.reason)
        } else {
          // 图表数据统一走 /fz/charts（默认 1Y 也从这里取）
          await fetchChartData()

          // 至少有一个请求成功，启动 WebSocket 订阅
          subscribePositionPrices()
        }
      } catch (err) {
        error.value = handleApiError(err)
      } finally {
        // 确保无论如何都会关闭加载状态
        loading.value = false
      }
    }

    // 订阅持仓明细的实时价格
    const subscribePositionPrices = () => {
      // 如果已经有 WebSocket 连接，则不再创建新的
      if (wsInstance) {
        console.log('WebSocket 连接已存在，跳过重复创建')
        return
      }
      
      if (positions.value.length === 0) {
        console.warn('持仓列表为空，无法订阅行情')
        return
      }
      
      try {
        console.log('开始创建 WebSocket 连接，持仓数量:', positions.value.length)
        
        // 创建 WebSocket 连接
        wsInstance = new WXQuotationWebSocket()
        
        // 连接成功后订阅
        wsInstance.on(WXQuotationWebSocket.EVENT.onOpen, () => {
          console.log('WebSocket 连接成功，开始订阅持仓明细行情')
          
          // 准备股票列表，延时行情需在 market 前加 DELAY_ 前缀
          const stockList = positions.value.map(position => {
            const symbol = (position as any).symbol
            let market = (position as any).market as string
            const isHK = market === 'HKSE'
            const isUS = market === 'NYSE' || market === 'NASDAQ' || market.includes('US')
            if (isHK && hkDelay) market = `DELAY_${market}`
            if (isUS && usDelay) market = `DELAY_${market}`
            console.log('准备订阅股票:', { market, symbol, code: position.code })
            return { market, symbol }
          })
          
          console.log('股票列表:', stockList)
          
          // 订阅列表行情
          if (wsInstance && stockList.length > 0) {
            wsInstance.safeSubscribeList('arkOnePositions', stockList)
            console.log('已发送订阅请求')
          }
        })
        
        // 监听行情数据更新
        wsInstance.on(WXQuotationWebSocket.EVENT.onHQLIST, (data: any) => {
          console.log('收到行情数据:', JSON.stringify(data))
          
          // 处理行情数据 - 可能是数组或单个对象
          let quoteDataList: any[] = []
          
          if (data && data.QuoteData) {
            if (Array.isArray(data.QuoteData.DynaData)) {
              quoteDataList = data.QuoteData.DynaData
            } else if (data.QuoteData.DynaData) {
              quoteDataList = [data.QuoteData.DynaData]
            }
          }
          
          console.log('处理行情数据列表:', quoteDataList)
          
          // 更新持仓明细中的现价
          quoteDataList.forEach((dynaData: any) => {
            console.log('处理单条行情:', { Inst: dynaData.Inst, Market: dynaData.Market, LastPrice: dynaData.LastPrice })
            
            positions.value.forEach(position => {
              const posSymbol = (position as any).symbol
              const posMarket = (position as any).market
              
              // 尝试多种匹配方式
              const isMatch = 
                (posSymbol === dynaData.Inst && posMarket === dynaData.Market) ||
                (posSymbol === dynaData.InstrumentID && posMarket === dynaData.ExchangeID)
              
              if (isMatch) {
                console.log('匹配成功，更新现价:', { 
                  code: position.code, 
                  oldPrice: position.currentPrice, 
                  newPrice: dynaData.LastPrice 
                })
                position.currentPrice = dynaData.LastPrice
              }
            })
          })
        })
        
        // 错误处理
        wsInstance.on(WXQuotationWebSocket.EVENT.onError, (err: any) => {
          console.error('WebSocket 错误:', err)
        })
        
        wsInstance.on(WXQuotationWebSocket.EVENT.onClose, () => {
          console.log('WebSocket 连接已关闭')
          wsInstance = null
        })
        
        // 开始连接
        wsInstance.connect()
        console.log('WebSocket 连接已启动')
      } catch (err) {
        console.error('订阅持仓明细行情失败:', err)
        wsInstance = null
      }
    }

    // 停止 WebSocket 订阅
    const stopSubscribePositionPrices = () => {
      if (wsInstance) {
        wsInstance.safeUnsubscribeList('arkOnePositions')
        wsInstance.close()
        wsInstance = null
      }
    }

    // 处理时间范围变化
    const handleTimeRangeChange = (range: TimeRange) => {
      selectedTimeRange.value = range
      // 时间范围变化时重新获取图表数据
      fetchChartData()
    }

    // 处理调仓标记点击
    const handleRebalancingMarkerClick = (date: string) => {
      selectedRebalancingDate.value = date
    }

    // 处理下单按钮点击
    const handleOrder = (position: Position) => {
      console.log('打印====,',position)
      try {
        const market = (position.market || '').toString()
        const symbol = (position.symbol || '').toString()
        const name = (position.name || '').toString()
        const exchange = (position.exchange || '').toString()
        const url = `gw://funccall?funcname=OnGoJiaoYi;market=${market};symbol=${symbol};type=1;name=${name};exchange=${exchange};future_type=;derivative_type=;is_stock_index=;ei=;`
        window.open(url)
      } catch (e) {
        console.error('下单失败:', e)
      }
    }

    // 处理股票详情导航
    const handleStockDetailNavigation = (data: Position | RebalancingRecord) => {
      try {
        const market = (data as any).market
        const symbol = (data as any).symbol
        const url = `gw://funccall?funcname=OnFireCommand;setcurrent=1;Command=fszs;param=<params><code>${symbol}</code><market_letter>${market}</market_letter><technique_type>0</technique_type><technique_name></technique_name><element_InfoTab><SubTabName></SubTabName></element_InfoTab><QUOTE_TAB_pt><SubTabName></SubTabName></QUOTE_TAB_pt><level2_orderchart_type>-1</level2_orderchart_type><StyleName>FSZS</StyleName><TabName>分时图</TabName><flag_container_style>R</flag_container_style></params>`
        window.open(url)
      } catch (e) {
        console.error('跳转到个股分时失败:', e)
      }
    }

    onMounted(() => {
      fetchAllData()
    })

    onBeforeUnmount(() => {
      stopSubscribePositionPrices()
      // 重置初始化标志
      isInitialized.value = false
    })

    // 监听数据加载完成后,转换页面文字
    watch(() => loading.value, (newVal) => {
      if (!newVal && isTraditional.value) {
        // 数据加载完成且是繁体模式,使用 nextTick 确保 DOM 已更新
        nextTick(() => {
          console.log('[ArkOne] 开始转换繁体')
          zh_tran('t')
        })
      }
    })

    return {
      theme,
      hkDelay,
      usDelay,
      loading,
      error,
      portfolioData,
      performanceData,
      positions,
      selectedTimeRange,
      selectedRebalancingDate,
      rebalancingDates,
      colorMode,
      language,
      fetchAllData,
      handleTimeRangeChange,
      handleRebalancingMarkerClick,
      handleOrder,
      handleStockDetailNavigation
    }
  }
})

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "e6516010": (_ctx.theme === "light" ? "#f5f6fa" : "#0a0a0a"),
  "0b25ff45": (_ctx.theme === "light" ? "#000000" : "#ffffff"),
  "5ccd23f2": (_ctx.theme === "light" ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.1)"),
  "27ad5289": (_ctx.theme === "light" ? "#1890ff" : "#1890ff"),
  "772d37c0": (_ctx.theme === "light" ? "#ffffff" : "#111424")
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__